import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { SLICE_NAME } from './unresolvedItems.constants';
import {
  IUnresolvedItemAnchor,
  IUnresolvedItemsState,
} from './unresolvedItems.props';

const initialState: IUnresolvedItemsState = {
  allDismissed: false,
  anchors: {},
  highlightedTranscriptMessageId: null,
};

export const unresolvedItemsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetUnresolvedItems: () => {
      return initialState;
    },
    dismissAllUnresolvedItems: (state) => {
      state.allDismissed = true;
    },
    // HITL-1621: record the triggering utterance for cards that just appeared.
    // First write wins — a card's anchor is the line that caused it, and must
    // not drift to later utterances when the cumulative list is re-sent.
    captureAnchors: (
      state,
      action: PayloadAction<Record<string, IUnresolvedItemAnchor>>
    ) => {
      Object.entries(action.payload).forEach(([key, anchor]) => {
        if (!(key in state.anchors)) {
          state.anchors[key] = anchor;
        }
      });
    },
    // HITL-1621: "Show in transcript" pulse target.
    setHighlightedTranscriptMessage: (state, action: PayloadAction<string>) => {
      state.highlightedTranscriptMessageId = action.payload;
    },
    // Clears only when the given id is still the highlighted one, so an older
    // card's delayed clear can't cut short a newer card's pulse.
    clearHighlightedTranscriptMessage: (
      state,
      action: PayloadAction<string>
    ) => {
      if (state.highlightedTranscriptMessageId === action.payload) {
        state.highlightedTranscriptMessageId = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Reset at the start of a new session so stale dismissals don't bleed
    // across sessions. The cumulative lists themselves live on the hypothesis
    // frame and shrink/clear when the producer resets them.
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const isNewSession = action.payload.messages.some(
          (message) =>
            message.event === EventTypes.startSession ||
            message.event === EventTypes.carEnter
        );
        if (isNewSession) {
          return initialState;
        }
      }
    );
  },
});

const {
  resetUnresolvedItems,
  dismissAllUnresolvedItems,
  captureAnchors,
  setHighlightedTranscriptMessage,
  clearHighlightedTranscriptMessage,
} = unresolvedItemsSlice.actions;

export {
  initialState,
  resetUnresolvedItems,
  dismissAllUnresolvedItems,
  captureAnchors,
  setHighlightedTranscriptMessage,
  clearHighlightedTranscriptMessage,
};
export default unresolvedItemsSlice.reducer;
